import { ChevronDown, ChevronRight } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { ActiveLocation } from '../model/active-location';
import type { ResolvedModule } from '../model/resolve-navigation';

/** The overview. It comes first and stands apart from the modules (Brand Kit § The frame). */
const OVERVIEW_ID = 'home';

/**
 * The two-level rail (proposal decision 1): a module at level one, the active module's own
 * sections nested beneath it at level two.
 *
 * ONE COMPONENT, TWO HOSTS. The persistent rail and the off-canvas drawer render this same
 * element, exactly as the previous shell shared one `navContent` between them, so the two can
 * never drift apart. What differs between them is not the markup but the CONTAINER: the drawer is
 * portalled outside `.app-shell`, so `shell.css`'s container queries do not match inside it and it
 * always gets the expanded rail — which is what a 340px drawer should show.
 *
 * EVERY MODULE'S SECTIONS ARE RENDERED; CSS DECIDES WHICH ARE SHOWN. In the expanded rail only the
 * active module's list is visible — an always-open tree of every module's sections is a wall of
 * twelve links where two matter. In the compressed rail every group's list becomes a flyout, so a
 * module stays reachable at section granularity without first entering it, which is the whole
 * reason the flyout exists. Rendering them all is what lets one CSS rule decide that, rather than
 * this component having to know its own container's width — which it cannot.
 *
 * `aria-current` COMES FROM THE MODEL, SO THESE ARE `Link`s AND NOT `NavLink`s. Two reasons, and
 * the second is a trap worth recording. `NavLink`'s own `isActive` marks the module by its landing
 * path (`/clinical-study/studies`), so standing on a study DETAIL would leave the module unmarked —
 * the model matches on the module's TERRITORY instead, and the breadcrumb and bottom bar read that
 * same answer (`active-location.ts`). And `NavLink` spreads its own computed `aria-current` AFTER
 * the caller's props, so a `NavLink` given a model-supplied `aria-current` silently drops it to
 * `undefined` whenever its own path match disagrees. Mixing the two sources looks like it works and
 * marks nothing.
 *
 * THE OVERVIEW, THEN "MODULI" (Brand Kit § The frame). `Panoramica` is the platform's own screen
 * rather than a module, so it comes first and the modules follow as a labelled group. The group is
 * a real `role="group"` named by its visible label, so a screen reader meets the same structure the
 * eye does; the compressed rail hides the label and keeps the group.
 */
export function ModuleRail({
  modules,
  active,
  onNavigate,
}: {
  modules: readonly ResolvedModule[];
  active: ActiveLocation;
  /** Called after any entry is activated — the drawer closes on it; the rail passes nothing. */
  onNavigate?: () => void;
}) {
  const { t } = useTranslation('shell');
  const groupLabelId = useId();

  const overviewCount = modules[0]?.id === OVERVIEW_ID ? 1 : 0;
  const overview = modules.slice(0, overviewCount);
  const rest = modules.slice(overviewCount);

  function renderModule(module: ResolvedModule) {
    const isActiveModule = active.module?.id === module.id;
    const hasSections = module.sections.length > 0;
    const Chevron = isActiveModule ? ChevronDown : ChevronRight;
    return (
      // `data-active` rather than a conditional class: `shell.css` reads it in two rules of
      // equal specificity whose source order decides the outcome, and an attribute keeps both
      // of them readable as one pair.
      <div className="rail-group" data-active={String(isActiveModule)} key={module.id}>
        <Link
          to={module.path}
          className="rail-module"
          onClick={onNavigate}
          // The name has to survive the compressed rail, where the label is `display: none`
          // and therefore gone from the accessibility tree — the standard requires every
          // compressed entry to keep "a distinguishing mark plus `title`/`aria-label`". Set at
          // every width rather than conditionally: the string is identical to the visible
          // label, so it changes nothing when the label is showing.
          //
          // The counter has to be spoken HERE or not at all: an `aria-label` replaces the
          // element's contents in the accessibility tree, so the badge's digits are invisible
          // to a screen reader no matter how they are marked up. The visible badge and the
          // spoken sentence are two renderings of one `AttentionCount`, never two sources.
          aria-label={
            module.count === undefined ? module.label : `${module.label}, ${module.count.label}`
          }
          title={module.label}
          aria-current={isActiveModule ? 'page' : undefined}
        >
          <span className="rail-module-glyph" aria-hidden="true">
            <module.icon />
          </span>
          <span className="rail-module-label">{module.label}</span>
          {module.count === undefined ? null : (
            // `aria-hidden`, because the same figure is already in the link's own name above.
            // Without it a screen reader reads "Studi clinici, 3 visite a rischio 3".
            <span className="rail-module-count" aria-hidden="true">
              {module.count.value}
            </span>
          )}
          {hasSections ? <Chevron className="rail-module-chevron" aria-hidden="true" /> : null}
        </Link>
        {hasSections ? (
          <div className="rail-sections">
            {module.sections.map((section) => (
              <Link
                key={section.id}
                to={section.path}
                className="rail-section"
                onClick={onNavigate}
                // Guarded on the module too: section ids are unique only WITHIN a module, and
                // every module's sections are in the DOM, so an id shared between two modules
                // would otherwise mark two rows current at once.
                aria-current={
                  isActiveModule && active.section?.id === section.id ? 'page' : undefined
                }
              >
                {section.label}
              </Link>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <nav className="rail-scroll" aria-label={t('nav.modules')}>
      {overview.map(renderModule)}
      {rest.length > 0 ? (
        <div className="rail-module-group" role="group" aria-labelledby={groupLabelId}>
          <p className="rail-group-label" id={groupLabelId}>
            {t('nav.modules')}
          </p>
          {rest.map(renderModule)}
        </div>
      ) : null}
    </nav>
  );
}
